import { WModalOverlay } from "@/components/modal/WModal.tsx";
import { niceOverlayProps } from "@/utils/niceOverlayProps.tsx";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import type { ReactElement, ReactNode } from "react";

/** Show a modal with the given React element as its body. */
export function showModal(body: ReactElement) {
    discardPromise(waitModal(body));
}

/** Wait for a modal with the given React element as its body to be closed. */
export async function waitModal(body: ReactElement) {
    return await NiceModal.show(ModalWrapper, { children: body });
}

const ModalWrapper = NiceModal.create(function ModalWrapper({ children }: { children: ReactNode }) {
    const modal = useModal();
    return <WModalOverlay {...niceOverlayProps(modal)}>{children}</WModalOverlay>;
});

/**
 * Register a modal {@link Component} manually with NiceModal. Useful when you only want to have a single instance.
 * @public
 * @example
 *  const RegisteredModal = registerModal(MyModalComponent);
 *  // Later, to show the modal:
 *  NiceModal.show(RegisteredModal, { someProp: value });
 */
export function registerModal<TProps extends {}>(Component: React.ComponentType<TProps>) {
    return NiceModal.create(function RegisterModalWrapper(props: TProps) {
        const modal = useModal();
        return (
            <WModalOverlay {...niceOverlayProps(modal)}>
                <Component {...props} />
            </WModalOverlay>
        );
    });
}
